// Pure helpers for the segment detail page's customer table and its
// client-side CSV export. No React, no gateway — unit-tested.

import type {
  SegmentMember,
  SegmentMembersSort,
} from '../../../types/loyaltySegments'

// ── Sort dropdown ────────────────────────────────────────────────────────────

export const DEFAULT_SEGMENT_MEMBERS_SORT: SegmentMembersSort = 'ltv'

// The mockup's `#detailSort` options, mapped onto the members endpoint's
// `sort` values.
export const SEGMENT_MEMBERS_SORT_OPTIONS: {
  value: SegmentMembersSort
  label: string
}[] = [
  { value: 'ltv', label: 'Highest LTV' },
  { value: 'orders', label: 'Most Orders' },
  { value: 'recent', label: 'Recently Active' },
  { value: 'name', label: 'A → Z' },
]

// ── Names ────────────────────────────────────────────────────────────────────

export const segmentMemberName = (member: SegmentMember): string =>
  `${(member.first_name ?? '').trim()} ${(member.last_name ?? '').trim()}`.trim() ||
  member.email ||
  '—'

// First + last initial; single-word names take their first two letters; a
// nameless member falls back to the email's first two characters.
export const segmentMemberInitials = (member: SegmentMember): string => {
  const first = (member.first_name ?? '').trim()
  const last = (member.last_name ?? '').trim()
  if (first && last) return (first[0] + last[0]).toUpperCase()
  const single = first || last
  if (single) return single.slice(0, 2).toUpperCase()
  return (member.email || '?').slice(0, 2).toUpperCase()
}

// ── Avatar colour ────────────────────────────────────────────────────────────

// The segment builder's colour-swatch palette (segmentBuilderRules'
// `COLOR_OPTIONS` hexes) — kept here as plain hex so this file stays
// React-free while both the builder's sample list and the detail page's
// customer table can hash a per-customer colour from it.
const AVATAR_COLOR_PALETTE = [
  '#1E7D4A',
  '#1D4ED8',
  '#7C3AED',
  '#0F766E',
  '#EA580C',
  '#B45309',
  '#DC2626',
  '#4338CA',
  '#BE185D',
  '#4B5563',
]

// Stable avatar colour hashed from a seed (customer name / email) — the same
// name always lands on the same swatch, and different names spread across
// the palette instead of every row sharing the segment's own colour.
export const avatarColor = (seed: string): string => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return AVATAR_COLOR_PALETTE[Math.abs(hash) % AVATAR_COLOR_PALETTE.length]
}

// ── Dates ────────────────────────────────────────────────────────────────────

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const parseIso = (iso: string | null | undefined): Date | null => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

// "Jan 19, 2024" — the mockup's `fmtDate`. '—' for null / invalid.
export const formatMemberDate = (iso: string | null | undefined): string => {
  const date = parseIso(iso)
  return date ? dateFormatter.format(date) : '—'
}

// The mockup's `daysAgo`: Today / Yesterday / Nd ago / Nw ago / Nmo ago /
// Ny ago. '' for null / invalid so the sub-line collapses.
export const formatDaysAgo = (
  iso: string | null | undefined,
  now: number = Date.now(),
): string => {
  const date = parseIso(iso)
  if (!date) return ''
  const d = (now - date.getTime()) / 86_400_000
  if (d < 1) return 'Today'
  if (d < 2) return 'Yesterday'
  if (d < 7) return `${Math.round(d)}d ago`
  if (d < 30) return `${Math.round(d / 7)}w ago`
  if (d < 365) return `${Math.round(d / 30)}mo ago`
  return `${Math.round(d / 365)}y ago`
}

// ── CSV export ───────────────────────────────────────────────────────────────

export const SEGMENT_MEMBERS_CSV_HEADER = [
  'Name',
  'Email',
  'City',
  'Province',
  'Orders',
  'Online Orders',
  'In-Store Orders',
  'Lifetime Value',
  'Avg Order Value',
  'Last Order',
  'Last Seen',
  'Points',
]

const money = (value: number): string => Number(value ?? 0).toFixed(2)

// One CSV row per member, in SEGMENT_MEMBERS_CSV_HEADER order. Dates are the
// raw ISO strings (spreadsheet-friendly), money is 2dp with no symbol.
export const segmentMemberCsvRow = (member: SegmentMember): string[] => [
  segmentMemberName(member),
  member.email ?? '',
  member.location?.city ?? '',
  member.location?.province ?? '',
  String(member.orders?.total ?? 0),
  String(member.orders?.online ?? 0),
  String(member.orders?.instore ?? 0),
  money(member.lifetime_value),
  money(member.avg_order_value),
  member.last_order_at ?? '',
  member.last_seen_at ?? '',
  String(member.points_balance ?? 0),
]

const escapeCsvCell = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

export const buildSegmentMembersCsv = (members: SegmentMember[]): string =>
  [SEGMENT_MEMBERS_CSV_HEADER, ...members.map(segmentMemberCsvRow)]
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\n')

// `Segment_VIP_2026-09-16.csv`
export const segmentMembersCsvFilename = (
  segmentName: string,
  date: Date = new Date(),
): string => {
  const safe = (segmentName || 'segment')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
  return `Segment_${safe || 'segment'}_${date.toISOString().slice(0, 10)}.csv`
}
